import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  catalogPriceInput,
  compareCatalogPrice,
  fueraDelFreno,
  nombreIngredienteLimpio,
  precioDePresentacion,
  PURCHASE_UNITS,
  REGLAS_ESCANER,
  type CatalogPriceLine,
  type CatalogPricesProposal,
  type DudaPrecio,
  type Ingredient,
  type InvoiceLineFormat,
  type PurchaseUnit,
} from '@alexiarest/shared';
import { api } from '../api/client';
import { useImportIngredients, useIngredients, useSuppliers } from '../api/hooks';
import { ingredientLabel } from '../ingredientLabel';
import { formatDecimalInput, formatMoney, formatQty, parseDecimal } from '../format';
import { unitLabel } from '../labels';
import { SmartSelect } from './Field';
import { presentacionDeIngrediente, presentacionDeLinea } from './escanerFormato';

/**
 * «Los productos» del escáner único (bloque S, 14 oct 2026; reglas 7 y 8 de
 * la sección 8b y revisión del 12 oct). Cada línea de la factura es UNA
 * línea, no un formulario:
 *
 *  - lo claro sale claro y no pide nada: «Precio igual», «Sube 5 %», «Baja 3 %»,
 *    «No es ingrediente»;
 *  - lo dudoso va en ámbar y pide UN toque: «¿Es este?», «Nuevo» con
 *    «Crearlo: pieza de 400 g, 1,05 €», o «Revisa el precio» cuando
 *    cantidad × precio unitario no da el importe de la línea, no se lee la
 *    cantidad o el precio nuevo es más del triple (o menos de un tercio);
 *  - debajo de cada una, en gris, lo que dice la factura;
 *  - los precios, SIN IVA y por la presentación de compra (nunca por kg).
 *
 * Alexia RECUERDA lo que el usuario decide (fase 7): «Sí, es ese» o elegir
 * otro ingrediente guarda el alias para ese proveedor, y «No es ingrediente»
 * también. Lo que queda sin decidir NO bloquea: al guardar, esa línea se
 * queda en «Por revisar» dentro de su factura.
 */

type Decision = 'actualizar' | 'crear' | 'pendiente' | 'elegir' | 'ignorar' | 'precio';

export interface LineaFactura {
  index: number;
  description: string;
  /** Texto de la línea sin tamaños ni paquetes («PAN CAJA BCO GDE»): con él se recuerda el alias. */
  texto: string;
  /** Nombre del ingrediente si se crea («Barra rustica»). */
  name: string;
  unit: string;
  /** Cuánto trae la presentación del precio (6 de «paquete de 6», 400 de «pieza de 400 g»). */
  qty: string;
  /** Precio SIN IVA de UNA presentación, tal cual se edita. */
  price: string;
  decision: Decision;
  ingredientId: string;
  /** Lo que propuso Alexia cuando no estaba segura («¿Es tu …?»). */
  sugerido?: string;
  bought?: InvoiceLineFormat;
  /** Cuántos se compraron (la cantidad de la línea). */
  cantidad?: number;
  envase?: string;
  /** Por qué la separó Alexia al leerla (limpieza, gas, «lo marcaste antes»…). */
  motivoInicial?: CatalogPriceLine['ignoreReason'];
  /** El usuario eligió el ingrediente a mano: se recuerda como alias. */
  aMano?: boolean;
  /** Por qué el precio no es de fiar todavía (cantidad × unitario ≠ importe…). */
  duda?: DudaPrecio;
  /** El usuario confirmó un precio dudoso. */
  precioOk?: boolean;
  /** El usuario dio por bueno un formato que no casa con el del ingrediente. */
  formatoOk?: boolean;
}

function inicial(proposal: CatalogPricesProposal | undefined): LineaFactura[] {
  return (proposal?.lines ?? []).map((line) => {
    const candidato = line.match.candidates[0]?.ingredientId ?? '';
    // Con el formato en la descripción («ACEITE 1LT», «PAQ 6U») el precio es de UNA presentación.
    const pack = !line.unit && line.bought?.packQty && line.bought.packUnit ? line.bought : undefined;
    const exacto = line.match.kind === 'exact';
    const decision: Decision = line.action === 'ignore' ? 'ignorar'
      : exacto && (line.duda || !line.unitPrice) ? 'precio'
      : exacto ? 'actualizar' : 'pendiente';
    return {
      index: line.index,
      description: line.description,
      texto: line.name,
      name: nombreIngredienteLimpio(line.name || line.description),
      unit: line.unit ?? pack?.packUnit ?? '',
      qty: pack ? String(pack.packQty) : '1',
      price: line.unitPrice == null ? '' : formatDecimalInput(line.unitPrice, 4),
      decision,
      ingredientId: exacto ? candidato : '',
      sugerido: line.match.kind === 'fuzzy' || line.match.kind === 'ambiguous' ? candidato : undefined,
      bought: line.bought,
      cantidad: line.qty,
      envase: line.bought?.envase,
      motivoInicial: line.action === 'ignore' ? line.ignoreReason : undefined,
      duda: line.duda,
    };
  });
}

function comparar(linea: LineaFactura, ingredientes: Ingredient[], ingredientId = linea.ingredientId) {
  const actual = ingredientes.find((i) => i.ingredientId === ingredientId);
  const precio = parseDecimal(linea.price, 0);
  const qty = parseDecimal(linea.qty, 1) || 1;
  if (!actual || !linea.unit || !(precio > 0)) return { actual, comparacion: undefined };
  return { actual, comparacion: compareCatalogPrice(actual, precio, qty, linea.unit as PurchaseUnit) };
}

export type Accion = 'sube' | 'baja' | 'igual' | 'formato' | 'crear' | 'falta' | 'ignorar';

/** Qué va a pasar con cada línea al guardar. `falta` = se queda en «Por revisar». */
function accionDe(linea: LineaFactura, ingredientes: Ingredient[]): Accion {
  if (linea.decision === 'ignorar') return 'ignorar';
  const precio = parseDecimal(linea.price, 0);
  if (linea.decision === 'crear') return linea.name.trim() && linea.unit && precio > 0 ? 'crear' : 'falta';
  if (linea.decision !== 'actualizar' || !linea.ingredientId) return 'falta';
  const { actual, comparacion } = comparar(linea, ingredientes);
  if (!actual || !linea.unit || !(precio > 0)) return 'falta';
  if (!comparacion) return linea.formatoOk ? 'formato' : 'falta';
  // Freno extra: más del triple o menos de un tercio, solo con un «sí» explícito.
  if (fueraDelFreno(comparacion.previous, comparacion.current) && !linea.precioOk) return 'falta';
  if (Math.abs(comparacion.changePct) <= REGLAS_ESCANER.umbralIgualPct) return 'igual';
  return comparacion.changePct > 0 ? 'sube' : 'baja';
}

const MOTIVOS: Record<string, string> = {
  limpieza: 'Parece de limpieza.',
  gas: 'Es gas.',
  servicio: 'Es un servicio.',
  envio: 'Es el envío.',
  desechable: 'Es desechable.',
  recordado: 'Lo marcaste así la otra vez.',
  abono: 'Es un abono.',
};

export function useFacturaLineas(proposal: CatalogPricesProposal | undefined) {
  const ingredientes = useIngredients().data ?? [];
  const importar = useImportIngredients();
  const qc = useQueryClient();
  const [para, setPara] = useState(proposal);
  const [lineas, setLineas] = useState<LineaFactura[]>(() => inicial(proposal));
  /*
   * Líneas ya aplicadas con éxito. Si después falla algo, reintentar
   * «Guardar» no vuelve a crear el mismo ingrediente.
   */
  const [hechas, setHechas] = useState<ReadonlySet<number>>(() => new Set());
  if (para !== proposal) {
    setPara(proposal);
    setLineas(inicial(proposal));
    setHechas(new Set());
  }
  const editar = (index: number, patch: Partial<LineaFactura>) =>
    setLineas((prev) => prev.map((l) => (l.index === index ? { ...l, ...patch } : l)));

  const acciones = lineas.map((l) => accionDe(l, ingredientes));
  const cuenta = (a: Accion[]) => acciones.filter((x, i) => a.includes(x) && !hechas.has(lineas[i].index)).length;
  const resumen = {
    total: lineas.length,
    actualizan: cuenta(['sube', 'baja', 'formato']),
    confirman: cuenta(['igual']),
    crean: cuenta(['crear']),
    porDecidir: cuenta(['falta']),
  };

  /**
   * Lo que Alexia aprende de esta factura: los emparejamientos hechos a mano
   * (alias del proveedor) y las líneas que el usuario separó o devolvió.
   * Nunca bloquea el guardado.
   */
  const recordar = async (supplierId: string | undefined) => {
    const alias = lineas.filter((l, i) => l.aMano && l.ingredientId && l.texto.trim() && ['sube', 'baja', 'igual', 'formato'].includes(acciones[i]));
    const separar = lineas.filter((l) => l.decision === 'ignorar' && !l.motivoInicial && l.texto.trim());
    const devolver = lineas.filter((l) => l.decision !== 'ignorar' && l.motivoInicial === 'recordado');
    try {
      if (alias.length) await api.rememberIngredientAliases(alias.map((l) => ({ ingredientId: l.ingredientId, text: l.texto.trim(), supplierId, source: 'factura' as const })));
      if (separar.length || devolver.length) {
        await api.updateNoIngredientLines({
          add: separar.map((l) => ({ text: l.texto.trim(), supplierId })),
          remove: devolver.map((l) => ({ text: l.texto.trim(), supplierId })),
        });
      }
      if (alias.length) void qc.invalidateQueries({ queryKey: ['ingredients'] });
    } catch (e) {
      console.warn('[factura] no se pudo recordar la corrección', e);
    }
  };

  /**
   * Aplica los precios claros y las altas, y recuerda las correcciones.
   * Devuelve los errores (ya en frase) y las líneas que quedan por decidir.
   */
  const aplicar = async (supplierId: string | undefined): Promise<{ errores: string[]; quedan: number[] }> => {
    const items: Array<{ id?: string; input: ReturnType<typeof catalogPriceInput> }> = [];
    const itemLinea: number[] = [];
    const confirmar: Array<{ id: string; linea: number }> = [];
    const quedan: number[] = [];
    lineas.forEach((l, i) => {
      if (hechas.has(l.index)) return;
      const accion = acciones[i];
      if (accion === 'falta') { quedan.push(l.index); return; }
      const precio = parseDecimal(l.price, 0);
      const qty = parseDecimal(l.qty, 1) || 1;
      const { actual, comparacion } = comparar(l, ingredientes);
      if (accion === 'igual' && actual) confirmar.push({ id: actual.ingredientId, linea: l.index });
      if ((accion === 'sube' || accion === 'baja') && actual && comparacion && actual.netPrice != null && actual.purchaseQty && actual.purchaseUnit) {
        // Se conserva cómo lo compras («Paquete de 6»): solo cambia el precio.
        items.push({ id: actual.ingredientId, input: catalogPriceInput(actual, actual.name, precioDePresentacion(actual.netPrice, comparacion), actual.purchaseQty, actual.purchaseUnit as PurchaseUnit) });
        itemLinea.push(l.index);
      }
      if (accion === 'formato' && actual) {
        items.push({ id: actual.ingredientId, input: catalogPriceInput(actual, actual.name, precio, qty, l.unit as PurchaseUnit) });
        itemLinea.push(l.index);
      }
      if (accion === 'crear') {
        items.push({ input: { ...catalogPriceInput(undefined, l.name.trim(), precio, qty, l.unit as PurchaseUnit), supplierId, ...(l.envase ? { presentation: l.envase } : {}) } });
        itemLinea.push(l.index);
      }
    });
    const errores: string[] = [];
    const ok = new Set(hechas);
    if (items.length) {
      const r = await importar.mutateAsync({ items });
      errores.push(...r.errores.map((e) => `${e.name}: ${e.message}`));
      const fallidos = new Set(r.errores.map((e) => e.index));
      itemLinea.forEach((linea, k) => { if (!fallidos.has(k)) ok.add(linea); else quedan.push(linea); });
    }
    for (const { id, linea } of confirmar) {
      try { await api.confirmIngredientPrice(id); ok.add(linea); } catch (e) { errores.push(e instanceof Error ? e.message : String(e)); quedan.push(linea); }
    }
    setHechas(ok);
    if (confirmar.length) void qc.invalidateQueries({ queryKey: ['ingredients'] });
    await recordar(supplierId);
    return { errores, quedan: quedan.sort((a, b) => a - b) };
  };

  return { lineas, editar, acciones, resumen, aplicar, aplicando: importar.isPending };
}

/** «se actualizan 2 precios, se confirma 1 y se crea 1 ingrediente». */
export function frasesProductos(r: ReturnType<typeof useFacturaLineas>['resumen']): string[] {
  const partes: string[] = [];
  if (r.actualizan) partes.push(`se ${r.actualizan === 1 ? 'actualiza 1 precio' : `actualizan ${r.actualizan} precios`}`);
  if (r.confirman) partes.push(`se ${r.confirman === 1 ? 'confirma 1' : `confirman ${r.confirman}`}`);
  if (r.crean) partes.push(`se ${r.crean === 1 ? 'crea 1 ingrediente' : `crean ${r.crean} ingredientes`}`);
  return partes;
}

/** «a, b y c». */
export function enumerar(partes: string[]): string {
  if (partes.length <= 1) return partes.join('');
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/** Lo que cuesta UNA presentación de la línea, dicho como se compra. */
function porCada(l: LineaFactura): string {
  const u = l.unit as PurchaseUnit;
  // Un tamaño de peso sin envase escrito es una pieza («BARRA RUSTICA 400G»).
  if (!l.envase && l.bought?.packQty && (l.bought.packUnit === 'g' || l.bought.packUnit === 'kg')) return 'la pieza';
  if (l.envase) return `${l.envase === 'pieza' ? 'la' : l.envase.endsWith('a') ? 'la' : 'el'} ${l.envase}`;
  if (u && u !== 'pz' && parseDecimal(l.qty, 1) === 1) return `el ${unitLabel(u)}`;
  return 'cada uno';
}

export function FacturaLineas({ estado }: { estado: ReturnType<typeof useFacturaLineas> }): JSX.Element {
  const ingredientes = useIngredients().data ?? [];
  const proveedores = useSuppliers().data ?? [];
  const opciones = ingredientes
    .map((i) => ({ value: i.ingredientId, label: ingredientLabel(i, proveedores) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'));
  const { lineas, editar, acciones, resumen } = estado;
  // Primero lo que pide algo (ámbar), luego lo claro.
  const orden = lineas.map((l, i) => ({ l, i })).sort((a, b) => Number(acciones[b.i] === 'falta') - Number(acciones[a.i] === 'falta'));

  return (
    <section className="esc-productos" aria-labelledby="esc-productos-titulo">
      <div className="esc-productos__titulo">
        <h3 id="esc-productos-titulo">Los productos</h3>
        <span>
          {resumen.total === 1 ? '1 producto' : `${resumen.total} productos`} · {resumen.porDecidir ? `${resumen.porDecidir} por decidir` : 'todo listo'}
        </span>
      </div>
      {orden.map(({ l, i }) => <Producto key={l.index} l={l} accion={acciones[i]} ingredientes={ingredientes} opciones={opciones} editar={editar} />)}
    </section>
  );
}

function Producto({ l, accion, ingredientes, opciones, editar }: {
  l: LineaFactura;
  accion: Accion;
  ingredientes: Ingredient[];
  opciones: Array<{ value: string; label: string }>;
  editar: (index: number, patch: Partial<LineaFactura>) => void;
}): JSX.Element {
  const ambar = accion === 'falta';
  const precio = parseDecimal(l.price, 0);
  const enFactura = (
    <p className="esc-prod__factura">En la factura: «{l.description}»{l.cantidad != null ? ` · ${formatQty(l.cantidad)}` : ''}</p>
  );
  const chip = (texto: string, tono: 'ambar' | 'verde' | 'gris') => <span className={`esc-chip esc-chip--${tono}`}>{texto}</span>;
  const enlace = (texto: string, patch: Partial<LineaFactura>) => (
    <button type="button" className="esc-enlace" onClick={() => editar(l.index, patch)}>{texto}</button>
  );
  const clase = `esc-prod${ambar ? ' is-ambar' : ''}${l.decision === 'ignorar' ? ' is-gris' : ''}`;

  /* --- No es ingrediente --- */
  if (l.decision === 'ignorar') {
    const motivo = l.motivoInicial ? MOTIVOS[l.motivoInicial] : undefined;
    return (
      <article className={clase}>
        <div className="esc-prod__cabeza"><strong>{l.name || l.description}</strong>{chip('No es ingrediente', 'gris')}</div>
        <p className="esc-prod__texto">{motivo ? `${motivo} ` : ''}Va solo al gasto. {enlace('Sí es un ingrediente', { decision: 'pendiente' })}</p>
        {enFactura}
      </article>
    );
  }

  /* --- Coincide: precio igual, sube, baja, o algo que revisar --- */
  if ((l.decision === 'actualizar' || l.decision === 'precio') && l.ingredientId) {
    const { actual, comparacion } = comparar(l, ingredientes);
    const nombre = actual?.name ?? l.name;
    const presentacion = actual ? presentacionDeIngrediente(actual) : '';
    const triple = comparacion && fueraDelFreno(comparacion.previous, comparacion.current) && !l.precioOk;
    if (l.decision === 'precio' || triple || (!comparacion && !l.formatoOk)) {
      const duda: DudaPrecio | 'unidad' | undefined = triple ? 'x3' : l.decision === 'precio' ? l.duda ?? 'sin-importe' : 'unidad';
      const antes = actual?.netPrice;
      const nuevo = actual && comparacion ? precioDePresentacion(actual.netPrice ?? 0, comparacion) : undefined;
      const frase = duda === 'x3' && antes != null && nuevo != null
        ? <>Antes {formatMoney(antes)} y ahora {formatMoney(nuevo)}: {nuevo > antes ? 'más del triple' : 'menos de un tercio'}. Casi seguro lo leí mal. ¿Cuánto cuesta {porCada(l)}?</>
        : duda === 'no-cuadra' ? <>No me cuadran las cuentas de esta línea (cantidad × precio no da el importe). ¿Cuánto cuesta {porCada(l)}?</>
        : duda === 'sin-cantidad' ? <>No leo cuántos compraste, así que no puedo comprobar el precio. ¿Cuánto cuesta {porCada(l)}?</>
        : duda === 'sin-importe' ? <>No veo el importe de la línea para comprobar el precio. ¿Cuánto cuesta {porCada(l)}?</>
        : <>No sé a qué cantidad corresponde el precio. ¿Cómo viene?</>;
      return (
        <article className={clase}>
          <div className="esc-prod__cabeza"><strong>{nombre}</strong>{chip(duda === 'unidad' ? '¿Cómo viene?' : 'Revisa el precio', 'ambar')}</div>
          <p className="esc-prod__texto">{presentacion ? `${presentacion} · ` : ''}{frase}</p>
          <UnidadYPrecio linea={l} editar={editar} conUnidad={duda === 'unidad' || !l.unit} />
          <div className="esc-prod__botones">
            <button type="button" className="btn btn--sm" disabled={!(precio > 0) || !l.unit}
              onClick={() => editar(l.index, { decision: 'actualizar', precioOk: true, ...(duda === 'unidad' ? { formatoOk: true } : {}) })}>
              {precio > 0 ? `Sí, ${formatMoney(precio)} ${porCada(l)}` : 'Escribe el precio'}
            </button>
          </div>
          <p className="esc-prod__texto">{enlace('No es este', { decision: 'elegir', ingredientId: '' })} · {enlace('No es ingrediente', { decision: 'ignorar' })}</p>
          {enFactura}
        </article>
      );
    }
    const cambio = comparacion ? Math.round(comparacion.changePct) : 0;
    const etiqueta = accion === 'igual' ? chip('Precio igual', 'verde')
      : accion === 'sube' ? chip(`Sube ${Math.max(1, cambio)} %`, 'ambar')
      : accion === 'baja' ? chip(`Baja ${Math.max(1, Math.abs(cambio))} %`, 'verde')
      : chip('Formato nuevo', 'ambar');
    const nuevo = actual && comparacion && actual.netPrice != null ? precioDePresentacion(actual.netPrice, comparacion) : precio;
    return (
      <article className={clase}>
        <div className="esc-prod__cabeza"><strong>{nombre}</strong>{etiqueta}</div>
        <p className="esc-prod__texto">
          {accion === 'igual'
            ? <>{presentacion} · {formatMoney(actual?.netPrice ?? nuevo)} · queda confirmado</>
            : accion === 'formato'
              ? <>Se guarda como viene en esta factura: {formatMoney(precio)} por {formatQty(parseDecimal(l.qty, 1), l.unit)}.</>
              : <>{presentacion} · antes <s>{formatMoney(actual?.netPrice ?? 0)}</s> → ahora <strong>{formatMoney(nuevo)}</strong></>}
          {l.aMano && <>. Y la próxima vez lo reconozco solo.</>}{' '}
          {l.aMano ? '' : '· '}{l.aMano || l.precioOk ? enlace('Deshacer', { decision: l.aMano ? 'pendiente' : l.duda ? 'precio' : 'actualizar', aMano: false, precioOk: false, formatoOk: false, ...(l.aMano ? { ingredientId: '' } : {}) }) : enlace('No es este', { decision: 'elegir', ingredientId: '' })}
        </p>
        {enFactura}
      </article>
    );
  }

  /* --- Se crea --- */
  const leida = presentacionDeLinea(l.bought, (l.unit || undefined) as PurchaseUnit | undefined, l.cantidad);
  const presentacionNueva = leida && l.unit ? leida.texto : l.unit ? `${formatQty(parseDecimal(l.qty, 1), l.unit)}` : '';
  if (l.decision === 'crear') {
    return (
      <article className={clase}>
        <div className="esc-prod__cabeza"><strong>{l.name}</strong>{accion === 'crear' ? chip('Se crea', 'verde') : chip('¿Cómo viene?', 'ambar')}</div>
        {accion === 'crear'
          ? <p className="esc-prod__texto">Se crea como ingrediente nuevo: {presentacionNueva}, {formatMoney(precio)}. {enlace('Deshacer', { decision: 'pendiente' })}</p>
          : <>
            <p className="esc-prod__texto">¿Cómo viene y cuánto cuesta? {enlace('Mejor no', { decision: 'pendiente' })}</p>
            <UnidadYPrecio linea={l} editar={editar} conUnidad />
          </>}
        {enFactura}
      </article>
    );
  }

  /* --- Elegir el ingrediente --- */
  if (l.decision === 'elegir') {
    return (
      <article className={clase}>
        <div className="esc-prod__cabeza"><strong>{l.name}</strong>{chip('¿Cuál es?', 'ambar')}</div>
        <p className="esc-prod__texto">Busca el ingrediente que es. La próxima vez lo reconozco solo.</p>
        <SmartSelect
          ariaLabel={`Ingrediente para «${l.description}»`}
          value={l.ingredientId}
          placeholder="Escribe para buscar un ingrediente…"
          onChange={(value) => editar(l.index, value ? { ingredientId: value, decision: 'actualizar', aMano: true } : { ingredientId: '' })}
          options={[{ value: '', label: 'Elegir ingrediente' }, ...opciones]}
        />
        <p className="esc-prod__texto">{enlace('Volver', { decision: 'pendiente' })}</p>
        {enFactura}
      </article>
    );
  }

  /* --- ¿Es este? / Nuevo --- */
  const sugerido = ingredientes.find((x) => x.ingredientId === l.sugerido);
  const crear = { decision: 'crear' as const, ...(leida ? { unit: leida.unit, qty: String(leida.qty), envase: leida.envase } : {}) };
  if (sugerido) {
    const conPres = presentacionDeIngrediente(sugerido);
    return (
      <article className={clase}>
        <div className="esc-prod__cabeza"><strong>¿{l.name}?</strong>{chip('¿Es este?', 'ambar')}</div>
        <p className="esc-prod__texto">
          ¿Es tu <strong>{sugerido.name}</strong>{conPres ? ` (${conPres.toLocaleLowerCase('es')})` : ''}?{precio > 0 ? ` Aquí viene a ${formatMoney(precio)} ${porCada(l)}.` : ''}
        </p>
        <AvisoPrecio l={l} editar={editar} />
        <div className="esc-prod__botones">
          <button type="button" className="btn btn--sm" onClick={() => editar(l.index, { decision: 'actualizar', ingredientId: sugerido.ingredientId, aMano: true })}>Sí, es ese</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => editar(l.index, { decision: 'elegir', ingredientId: '' })}>Es otro</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => editar(l.index, crear)}>Crear nuevo</button>
        </div>
        <p className="esc-prod__texto">{enlace('No es ingrediente', { decision: 'ignorar' })}</p>
        {enFactura}
      </article>
    );
  }
  return (
    <article className={clase}>
      <div className="esc-prod__cabeza"><strong>{l.name}</strong>{chip('Nuevo', 'ambar')}</div>
      <p className="esc-prod__texto">
        No está en tus ingredientes.{leida && precio > 0 ? ` ${mayus(leida.compra)} a ${formatMoney(precio)}${leida.envase ? '' : ` ${porCada(l)}`}.` : precio > 0 ? ` ${formatMoney(precio)} cada uno.` : ''}
      </p>
      <AvisoPrecio l={l} editar={editar} />
      <div className="esc-prod__botones">
        {leida && precio > 0
          ? <button type="button" className="btn btn--sm" onClick={() => editar(l.index, crear)}>Crearlo: {leida.texto}, {formatMoney(precio)}</button>
          : <button type="button" className="btn btn--sm" onClick={() => editar(l.index, { decision: 'crear' })}>Crearlo</button>}
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => editar(l.index, { decision: 'elegir' })}>Es uno que ya tengo</button>
      </div>
      <p className="esc-prod__texto">{enlace('No es ingrediente', { decision: 'ignorar' })}</p>
      {enFactura}
    </article>
  );
}

const mayus = (t: string): string => t.charAt(0).toLocaleUpperCase('es') + t.slice(1);

/** ⚠️ Precio unitario: lo que no se pudo comprobar se dice, también en una línea nueva. */
function AvisoPrecio({ l, editar }: { l: LineaFactura; editar: (index: number, patch: Partial<LineaFactura>) => void }): JSX.Element | null {
  if (!l.duda || l.precioOk) return null;
  const frase = l.duda === 'no-cuadra' ? 'No me cuadran las cuentas de esta línea (cantidad × precio no da el importe).'
    : l.duda === 'sin-cantidad' ? 'No leo cuántos compraste, así que no puedo comprobar el precio.'
    : 'No veo el importe de la línea para comprobar el precio.';
  return (
    <>
      <p className="esc-prod__texto esc-ambar-texto">{frase} Revisa cuánto cuesta {porCada(l)} antes de crearlo.</p>
      <UnidadYPrecio linea={l} editar={editar} />
    </>
  );
}

function UnidadYPrecio({ linea, editar, conUnidad }: { linea: LineaFactura; editar: (index: number, patch: Partial<LineaFactura>) => void; conUnidad?: boolean }) {
  return (
    <div className="esc-prod__fila">
      <label className="esc-prod__campo">
        <span>Precio sin IVA</span>
        <input className="input" inputMode="decimal" value={linea.price} onChange={(e) => editar(linea.index, { price: e.target.value })} />
      </label>
      {conUnidad && (
        <>
          <label className="esc-prod__campo">
            <span>por</span>
            <input className="input" inputMode="decimal" aria-label="Cantidad del formato" value={linea.qty} onChange={(e) => editar(linea.index, { qty: e.target.value })} />
          </label>
          <div className="esc-prod__campo">
            <span>Unidad</span>
            <SmartSelect
              ariaLabel="Unidad del precio"
              value={linea.unit}
              onChange={(value) => editar(linea.index, { unit: value })}
              options={[{ value: '', label: 'Unidad' }, ...PURCHASE_UNITS.map((u) => ({ value: u, label: unitLabel(u as PurchaseUnit) }))]}
            />
          </div>
        </>
      )}
    </div>
  );
}
